// Calendar chip edit modal. Renders an editable form (crew + start +
// end) for an existing assignment, mirroring the JobScheduleSection
// pattern on JobDetailPage. Shared between week and month calendar
// views so both surfaces expose the same edit affordance.

import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { MapPin, Plus, Trash2, CheckCircle2, RotateCcw } from 'lucide-react';
import type {
  CalendarAssignmentRow,
  CalendarUnassignedResponse,
  CalendarUnassignedJob,
  JobStatus,
} from '@gutterinv/shared';
import {
  patchCalendarAssignment,
  deleteCalendarAssignment,
  patchJob,
  postPhaseComplete,
  postPhaseUncomplete,
  setAssignmentComplete,
} from '../api/client.js';
import { installToggleState } from '../lib/assignment-complete.js';
import { PeopleMultiSelect } from './PeopleMultiSelect.js';
import { Button } from './ui/button.js';
import { Input } from './ui/input.js';
import { cn } from '../lib/utils.js';
import { Dialog, DialogContent, DialogTitle } from './ui/dialog.js';
import { todayIso, isPastDate, formatDate } from '../lib/format.js';
import { isPostInstallStatus } from '../lib/calendar-dates.js';

export function AssignmentEditModal({
  assignment,
  onClose,
}: {
  assignment: CalendarAssignmentRow;
  onClose: () => void;
}) {
  const queryClient = useQueryClient();

  // Phase 2.2: people are the unit of assignment. The legacy sibling-crew
  // exclusion is gone — a person may appear on multiple assignments; the
  // assignment-level uniqueness (job, purpose, start-date) is enforced
  // server-side. PeopleMultiSelect fetches its own people + crew presets.

  const origPeopleIds = assignment.people.map((p) => p.id);
  const [personIds, setPersonIds] = useState<string[]>(origPeopleIds);
  const [startDate, setStartDate] = useState(assignment.assignedDateStart);
  const [endDate, setEndDate] = useState(assignment.assignedDateEnd);

  const isEstimate = assignment.purpose === 'estimate';
  // Migration 0043: insulation rows are phase visits. Crew assignment is
  // owned by the Phases card (creates phase + items atomically). Office
  // can still date-edit here, but crew picker is hidden + an inline link
  // routes them to the Phases card for crew changes.
  const isInsulation = assignment.phase != null;
  // Worker-level lock: status is at/past installed → can't POST/PATCH/DELETE.
  const isInstalled = isPostInstallStatus(assignment.job.status);
  // Past-due install: scheduled date window has elapsed but the job
  // hasn't been advanced to 'installed' (crew may have forgotten to
  // confirm via Telegram). Surface a callout linking to the job page
  // where the office can flip status → installed manually.
  const isPastDueInstall =
    assignment.purpose === 'install' &&
    assignment.job.status === 'scheduled' &&
    isPastDate(assignment.assignedDateEnd);
  // A completed activity is FULLY FROZEN — no field edits, no Unassign —
  // until reverted. Completion is per-assignment completed_at (estimates +
  // insulation phases) OR job status at/past installed (gutter installs,
  // whose completion IS the status transition). The revert toggle below is
  // the only way to reopen, when one is available (e.g. an estimate with an
  // install already scheduled has no revert → it stays frozen by design).
  const isCompleted = assignment.completedAt !== null;
  const isLocked = isInstalled || isCompleted;
  // completedAt is the source of truth for the moment the action finished.
  const completedAtLabel = assignment.completedAt
    ? formatDate(assignment.completedAt)
    : null;
  const lockNotice = isInstalled
    ? completedAtLabel
      ? `Installed on ${completedAtLabel}. Locked — revert to Scheduled from the Job page to edit.`
      : 'Installed — locked. Revert to Scheduled from the Job page to edit.'
    : completedAtLabel
      ? `Completed on ${completedAtLabel}. Locked — use the revert button above to reopen for editing.`
      : 'Completed — locked. Use the revert button above to reopen for editing.';
  const startInvalid = isPastDate(startDate);
  const endInvalid = !isEstimate && !!endDate && endDate < startDate;
  const peopleDirty =
    personIds.length !== origPeopleIds.length ||
    personIds.some((id) => !origPeopleIds.includes(id));
  const dirty =
    peopleDirty ||
    startDate !== assignment.assignedDateStart ||
    (!isEstimate && endDate !== assignment.assignedDateEnd);

  const patchMutation = useMutation({
    mutationFn: () => {
      const patch: { personIds?: string[]; startDate?: string; endDate?: string } = {};
      if (peopleDirty) patch.personIds = personIds;
      if (
        startDate !== assignment.assignedDateStart ||
        (!isEstimate && endDate !== assignment.assignedDateEnd)
      ) {
        patch.startDate = startDate;
        patch.endDate = isEstimate ? startDate : endDate;
      }
      return patchCalendarAssignment(assignment.id, patch);
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['calendar', 'assignments'] });
      void queryClient.invalidateQueries({ queryKey: ['calendar', 'unassigned'] });
      void queryClient.invalidateQueries({ queryKey: ['jobs'] });
      // Phase 1.3 Item 11 — Item 10's items-list + new crew-change for
      // insulation means a PATCH here affects the Phases card + Schedule
      // card + Invoices list for this job. Bust those query keys so other
      // tabs reflect without manual refresh. Mirrors PhaseUI invalidateAll.
      if (isInsulation) {
        void queryClient.invalidateQueries({ queryKey: ['job-phases', assignment.job.id] });
        void queryClient.invalidateQueries({ queryKey: ['job', assignment.job.id] });
        void queryClient.invalidateQueries({ queryKey: ['job-invoices', assignment.job.id] });
      }
      onClose();
    },
  });

  const unassignMutation = useMutation({
    mutationFn: () => deleteCalendarAssignment(assignment.id),
    onMutate: async () => {
      await queryClient.cancelQueries({ queryKey: ['calendar'] });
      const prevAssignments = queryClient.getQueriesData<CalendarAssignmentRow[]>({
        queryKey: ['calendar', 'assignments'],
      });
      const prevUnassigned = queryClient.getQueryData<CalendarUnassignedResponse>([
        'calendar',
        'unassigned',
      ]);
      queryClient.setQueriesData<CalendarAssignmentRow[]>(
        { queryKey: ['calendar', 'assignments'] },
        (old) => old?.filter((a) => a.id !== assignment.id) ?? [],
      );
      const jobRow: CalendarUnassignedJob = {
        id: assignment.job.id,
        displayName: assignment.job.displayName,
        address: assignment.job.address,
        status: assignment.job.status,
        customerName: assignment.job.customerName,
        quoteAmountCents: assignment.job.quoteAmountCents,
      };
      queryClient.setQueryData<CalendarUnassignedResponse>(
        ['calendar', 'unassigned'],
        (old) => {
          const base: CalendarUnassignedResponse = old ?? { installs: [], estimates: [] };
          if (assignment.purpose === 'install') {
            return { ...base, installs: [...base.installs, jobRow] };
          }
          return { ...base, estimates: [...base.estimates, jobRow] };
        },
      );
      onClose();
      return { prevAssignments, prevUnassigned };
    },
    onError: (_err, _v, context) => {
      if (context?.prevAssignments) {
        for (const [key, data] of context.prevAssignments) {
          queryClient.setQueryData(key, data);
        }
      }
      if (context?.prevUnassigned) {
        queryClient.setQueryData<CalendarUnassignedResponse>(
          ['calendar', 'unassigned'],
          context.prevUnassigned,
        );
      }
    },
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey: ['calendar'] });
      void queryClient.invalidateQueries({ queryKey: ['jobs'] });
      // Phase 1.4 Item 15: for insulation rows the server cascade
      // (Phase 1.3 follow-up) drops the parent phases row too, so the
      // Install Phases card on JobDetailPage must refetch. Mirrors the
      // patchMutation fanout above.
      if (isInsulation) {
        void queryClient.invalidateQueries({ queryKey: ['job-phases', assignment.job.id] });
        void queryClient.invalidateQueries({ queryKey: ['job', assignment.job.id] });
        void queryClient.invalidateQueries({ queryKey: ['job-invoices', assignment.job.id] });
      }
    },
  });

  // Complete / revert checkmark (mirrors warranty + tasks). Gutter installs
  // toggle the JOB status (scheduled<->installed) via patchJob; insulation
  // phases toggle the per-phase complete/uncomplete endpoints. Estimates +
  // invoiced/completed jobs get no toggle (see installToggleState).
  const toggle = installToggleState({
    purpose: assignment.purpose,
    jobStatus: assignment.job.status,
    isPhase: isInsulation,
    assignmentCompleted: assignment.completedAt !== null,
    hasInstallAssignment: assignment.job.hasInstallAssignment,
  });
  const [toggleError, setToggleError] = useState<string | null>(null);

  const statusToggleMutation = useMutation({
    mutationFn: (): Promise<unknown> => {
      if (!toggle.show) return Promise.resolve(undefined);
      if (toggle.kind === 'phase') {
        // Insulation MUST route through the phase endpoint — a patchJob coarse
        // revert would clear only ja.completed_at, leaving jai.installed_at set
        // (half-revert). The phase uncomplete clears the full set + recomputes.
        const phaseId = assignment.phase!.id;
        return toggle.isRevert
          ? postPhaseUncomplete(assignment.job.id, phaseId, crypto.randomUUID())
          : postPhaseComplete(assignment.job.id, phaseId, crypto.randomUUID());
      }
      if (toggle.kind === 'estimate') {
        // Estimate visits complete per-assignment (own completed_at) — the
        // "crew did the measure visit" marker; no job-status change.
        return setAssignmentComplete(assignment.id, !toggle.isRevert);
      }
      // Gutter install completion IS the job status transition.
      const nextStatus: JobStatus = toggle.isRevert ? 'scheduled' : 'installed';
      return patchJob(assignment.job.id, { status: nextStatus });
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['calendar', 'assignments'] });
      void queryClient.invalidateQueries({ queryKey: ['calendar', 'unassigned'] });
      void queryClient.invalidateQueries({ queryKey: ['jobs'] });
      void queryClient.invalidateQueries({ queryKey: ['job', assignment.job.id] });
      if (isInsulation) {
        void queryClient.invalidateQueries({ queryKey: ['job-phases', assignment.job.id] });
        void queryClient.invalidateQueries({ queryKey: ['job-invoices', assignment.job.id] });
      }
      onClose();
    },
    onError: (err) => {
      // Detect the phase invoice-guard specifically; everything else (incl. a
      // stale out-of-map transition when the job changed in another tab) gets a
      // generic friendly message + a refetch so the toggle re-derives.
      const code = (err as { code?: string }).code;
      setToggleError(
        code === 'phase_has_invoice'
          ? "Can't revert — this phase has an invoice. Void it first."
          : "Couldn't update — the job may have changed. Refreshing…",
      );
      void queryClient.invalidateQueries({ queryKey: ['calendar', 'assignments'] });
      void queryClient.invalidateQueries({ queryKey: ['jobs'] });
    },
  });

  const busy =
    patchMutation.isPending || unassignMutation.isPending || statusToggleMutation.isPending;

  const peopleLabel =
    assignment.people.length > 0
      ? assignment.people.map((p) => p.name).join(', ')
      : assignment.crew.name;
  const saveDisabled =
    !dirty ||
    busy ||
    personIds.length === 0 ||
    !startDate ||
    (!isEstimate && !endDate) ||
    startInvalid ||
    endInvalid;

  return (
    <Dialog
      open
      onOpenChange={(next) => {
        if (!next) onClose();
      }}
    >
      <DialogContent className="w-[560px] max-w-[calc(100vw-2rem)] p-8 space-y-6" hideClose>
        <DialogTitle className="sr-only">Edit assignment</DialogTitle>
        <div>
          <div className="flex items-start justify-between gap-3">
            <div>
              {isInsulation && assignment.phase && (
                <div className="mb-1 text-xs font-semibold uppercase tracking-wider text-primary">
                  Edit Phase {assignment.phase.phaseNumber} — dates + crew
                </div>
              )}
              <Link
                to={`/jobs/${assignment.job.id}`}
                className="inline-block text-base font-semibold text-foreground hover:text-primary hover:underline"
                title="Open this job"
              >
                {assignment.job.displayName}
              </Link>
              <div className="mt-1 text-sm text-muted-foreground">
                {assignment.job.customerName}
              </div>
              {assignment.job.address && (
                <div className="mt-1 flex items-center gap-1.5 text-xs text-muted-foreground">
                  <MapPin className="h-3 w-3 shrink-0" />
                  {assignment.job.address}
                </div>
              )}
            </div>
            {/* Complete / revert checkmark — gutter installs toggle the whole
                job's status; insulation phases toggle the per-phase endpoint;
                estimates toggle the assignment's completed_at (measure-visit
                marker, no status change). Hidden for invoiced/completed installs
                + post-install/disqualified estimates (see toggle). */}
            {toggle.show && (
              <Button
                variant="outline"
                disabled={busy}
                onClick={() => statusToggleMutation.mutate()}
                title={
                  toggle.isRevert
                    ? toggle.kind === 'estimate'
                      ? 'Revert — mark estimate visit not done'
                      : toggle.kind === 'phase'
                        ? 'Revert this phase'
                        : 'Revert install (whole job → Scheduled)'
                    : toggle.kind === 'estimate'
                      ? 'Mark estimate visit complete'
                      : toggle.kind === 'phase'
                        ? 'Mark phase complete'
                        : 'Mark install complete (whole job)'
                }
              >
                {toggle.isRevert ? (
                  <RotateCcw className="h-4 w-4" />
                ) : (
                  <CheckCircle2 className="h-4 w-4" />
                )}
              </Button>
            )}
          </div>
          {isInsulation && !isLocked && (
            <p className="mt-2 text-[0.6875rem] text-muted-foreground">
              Items are managed by the Install Phases card on the Job page. You can change crew + dates here.
            </p>
          )}
          {/* Phase 1.3 Item 10: structured items list with quantities.
              Reverses /architect Phase 1.1 SEV3-7 "keep modal compact"
              — turns out office wants qty in both calendar modals.
              Falls back to itemsSummary if items absent (defensive). */}
          {isInsulation && assignment.phase?.items && assignment.phase.items.length > 0 ? (
            <ul className="mt-2 space-y-0.5">
              {assignment.phase.items.map((it, idx) => (
                <li
                  key={`${idx}-${it.name}`}
                  className="truncate text-[0.6875rem] text-muted-foreground"
                  title={`${it.name} × ${String(it.quantity)} ${it.unit}`}
                >
                  {it.name} × {String(it.quantity)} {it.unit}
                </li>
              ))}
            </ul>
          ) : (
            isInsulation && assignment.phase?.itemsSummary && (
              <p
                className="mt-1 truncate text-[0.6875rem] text-muted-foreground"
                title={assignment.phase.itemsSummary}
              >
                Items: {assignment.phase.itemsSummary}
              </p>
            )
          )}
        </div>

        {isPastDueInstall && (
          <div className="rounded-md border border-amber-300 bg-amber-50 px-3 py-3 text-xs text-amber-900 space-y-2">
            <div>
              This install was scheduled for{' '}
              <span className="font-semibold">
                {assignment.assignedDateStart === assignment.assignedDateEnd
                  ? formatDate(assignment.assignedDateStart)
                  : `${formatDate(assignment.assignedDateStart)} – ${formatDate(assignment.assignedDateEnd)}`}
              </span>{' '}
              but hasn't been marked installed yet. The crew may have
              forgotten to confirm via Telegram.
            </div>
            <Link
              to={`/jobs/${assignment.job.id}`}
              onClick={onClose}
              className="inline-flex items-center gap-1 font-semibold text-amber-900 underline underline-offset-2 hover:text-amber-700 no-underline"
            >
              Open job to mark Installed →
            </Link>
          </div>
        )}

        {isLocked ? (
          <div className="rounded-md border border-border bg-secondary/40 px-3 py-2 text-xs text-muted-foreground">
            {lockNotice}
          </div>
        ) : (
          <div className="space-y-4 rounded-md border border-border bg-secondary/30 p-4">
            {/* Phase 2.2: people picker replaces the single-crew chips.
                Renders for both gutter + insulation. */}
            <div>
              <label className="eyebrow mb-1 block">People</label>
              <PeopleMultiSelect value={personIds} onChange={setPersonIds} />
            </div>

            {isEstimate ? (
              <div>
                <label className="eyebrow mb-1 block">Date</label>
                <Input
                  type="date"
                  value={startDate}
                  onChange={(e) => setStartDate(e.target.value)}
                  min={todayIso()}
                  aria-invalid={startInvalid}
                  className={cn(
                    'h-8 text-xs',
                    startInvalid && 'border-destructive focus-visible:ring-destructive',
                  )}
                  disabled={patchMutation.isPending}
                />
              </div>
            ) : (
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <label className="eyebrow mb-1 block">Start Date</label>
                  <Input
                    type="date"
                    value={startDate}
                    onChange={(e) => {
                      const v = e.target.value;
                      setStartDate(v);
                      if (endDate && v > endDate) setEndDate(v);
                    }}
                    min={todayIso()}
                    aria-invalid={startInvalid}
                    className={cn(
                      'h-8 text-xs',
                      startInvalid && 'border-destructive focus-visible:ring-destructive',
                    )}
                    disabled={patchMutation.isPending}
                  />
                </div>
                <div>
                  <label className="eyebrow mb-1 block">End Date</label>
                  <Input
                    type="date"
                    value={endDate}
                    onChange={(e) => setEndDate(e.target.value)}
                    min={startDate || todayIso()}
                    aria-invalid={endInvalid}
                    className={cn(
                      'h-8 text-xs',
                      endInvalid && 'border-destructive focus-visible:ring-destructive',
                    )}
                    disabled={patchMutation.isPending || !startDate}
                  />
                </div>
                {(startInvalid || endInvalid) && (
                  <p className="col-span-2 -mt-1 text-[0.6875rem] text-destructive">
                    {startInvalid
                      ? 'Start date cannot be in the past.'
                      : 'End date must be on or after start date.'}
                  </p>
                )}
              </div>
            )}

            {patchMutation.error && (
              <div className="text-xs text-destructive">
                {patchMutation.error instanceof Error
                  ? patchMutation.error.message
                  : 'Save failed'}
              </div>
            )}
          </div>
        )}

        {/* "Schedule another crew on this job" — links to the Job
            detail page where JobScheduleSection's "+ Schedule Job"
            flow handles multi-crew creation. Hidden for post-install
            statuses (installed / invoiced / completed) since the
            worker would reject any new POST with INSTALLED_LOCKED. */}
        {!isInstalled && (
          <Link
            to={`/jobs/${assignment.job.id}`}
            onClick={onClose}
            className="flex items-center justify-center gap-1.5 rounded-md border border-dashed border-border px-3 py-2 text-xs font-medium text-muted-foreground transition-colors hover:border-primary/40 hover:text-primary no-underline"
          >
            <Plus className="h-3.5 w-3.5" />
            Schedule another crew on this job
          </Link>
        )}

        {/* Crew dot + date footer when locked — at-a-glance context for
            the read-only view. */}
        {isLocked && (
          <div className="flex items-center gap-2 text-xs text-muted-foreground">
            <span
              className="inline-block h-2.5 w-2.5 rounded-full"
              style={{ backgroundColor: assignment.crew.color }}
            />
            {peopleLabel} ·{' '}
            {assignment.assignedDateStart === assignment.assignedDateEnd
              ? formatDate(assignment.assignedDateStart)
              : `${formatDate(assignment.assignedDateStart)} – ${formatDate(assignment.assignedDateEnd)}`}
          </div>
        )}

        {toggleError && (
          <p className="text-xs text-destructive">{toggleError}</p>
        )}

        <div className="flex gap-2">
          {/* Save: only when not locked. */}
          {!isLocked && (
            <Button
              className="flex-1"
              disabled={saveDisabled}
              onClick={() => patchMutation.mutate()}
            >
              {patchMutation.isPending ? 'Saving...' : 'Save'}
            </Button>
          )}
          {/* Unassign: only on unlocked rows. A completed/installed activity
              is fully frozen — revert first to remove it. Server enforces the
              same (completed_at + INSTALLED_LOCKED guards on DELETE). */}
          {!isLocked && (
            <Button
              variant="destructive"
              disabled={busy}
              onClick={() => unassignMutation.mutate()}
              title="Unassign — removes this assignment"
            >
              <Trash2 className="h-4 w-4" />
            </Button>
          )}
          <Button variant="outline" className="flex-1" onClick={onClose}>
            {isLocked ? 'Close' : 'Cancel'}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
